<template>
  <header class="header def-header">
    <div class="header-left">
      <!-- Show Back Button or Sidebar Toggle -->
      <button type="button" :aria-label="$t('button.back')" v-if="showBackButton" @click="goBack" class="back-button">
        <font-awesome-icon :icon="['fas', 'arrow-left']" aria-hidden="true" />
      </button>
      <ToggleSidebar
        v-if="config.TOGGLE_SIDEBAR.enabled && !showBackButton">
      </ToggleSidebar>
      <button type="button" :aria-label="$t('button.downloads')" v-if="showDownloadButton" @click="goToDownloads" class="back-button">
        <font-awesome-icon :icon="['fas', 'download']" class="download-icon" aria-hidden="true" />
      </button>
    </div>

    <!-- Middle Section: Current Page Title -->
    <div class="header-middle">
      <h1 class="page-title" :aria-label="pageTitleAriaLabel">
        {{ pageTitle }}
      </h1>
      <span v-if="showMetaPageSub" class="meta-page-sub">({{ resolvedMetaPageSub }})</span>
    </div>

    <!-- Right Section: Logo -->
    <div class="header-right">
      <logo-image :class="logoImageClass" id="logo-image-wrapper" role="presentation"></logo-image>
    </div>
  </header>
</template>

<script>
  import ToggleSidebar from "@/components/shell/ToggleSidebar.vue";
  import LogoImage from "@/components/sub-components/LogoImage.vue";
  import {useAppStore} from "@/stores/app";

  export default {
    name: 'Header',
    components: {
      ToggleSidebar,
      LogoImage,
    },
    setup() {
      const appStore = useAppStore();

      return {
        appStore,
      };
    },
    data() {
      return {
        currentPageTitle: '',
        metaPageSub: undefined,
        didPageBackWork: true,
        pageBackTimer: undefined,
      };
    },
    inject:['getPageMetaTitle','resetPageMetaTitle','provideWith'],
    provide(){
      this.provideWith('goBack',this.goBack);
      return {};
    },
    computed: {
      config() {
        return window.config;
      },
      pageTitle() {
        return this.$t(this.$route.meta.title || '');
      },
      pageTitleAriaLabel() {
        const ariaTitleKey = this.$route && this.$route.meta ? this.$route.meta.ariaTitle : null;
        return ariaTitleKey ? this.$t(ariaTitleKey) : null;
      },
      showBackButton() {
        const hasBackButton = this.$route.meta && this.$route.meta.showBackButton && this.$router.history.current !== this.$router.history.base;
        return hasBackButton && !this.appStore.isExternalEntryRootRoute(this.$route.name);
      },
      showDownloadButton() {
        return this.$route.meta && this.$route.meta.showDownloadButton && this.$router.history.current !== this.$router.history.base;
      },
      logoImageClass(){
        let defaultClasses=['logo'];
        return defaultClasses.join(" ");
      },
      resolvedMetaPageSub() {
        const injectedValue = this.getPageMetaTitle;

        if (typeof injectedValue === 'function') {
          return injectedValue() || '';
        }

        if (injectedValue && typeof injectedValue === 'object' && 'value' in injectedValue) {
          return injectedValue.value || '';
        }

        return injectedValue || '';
      },
      showMetaPageSub() {
        return String(this.resolvedMetaPageSub).length > 0;
      },
    },
    methods: {
      getGuardedBackRoute() {
        if (!this.$route) {
          return null;
        }

        const routeMap = {
          OrderMenuCheckout: 'OrderMenuPlanner',
          MealPlanCheckout: 'MealPlanner',
          OrderMenuPlanner: 'OrderMenuParameters',
          MealPlanner: 'MealPlanParameters',
        };

        return routeMap[this.$route.name] || null;
      },
      navigateToRouteName(routeName) {
        if (!routeName) {
          return;
        }

        this.$navigate(routeName, {withReplace: true});
      },
      goBack() {
        const guardedBackRoute = this.getGuardedBackRoute();
        if (this.appStore.externalBackGuardArmed && guardedBackRoute) {
          this.didPageBackWork = false;
          this.navigateToRouteName(guardedBackRoute);
          return;
        }

        this.didPageBackWork = false;
        let baseBack = -1;
        const hash = this.$route.hash;
        if (hash && hash === '#main') { // todo: could be better implemented
          baseBack -= 1;
        }
        this.$router.go(baseBack); // Navigate back to the previous page
        this.pageBackTimer = setTimeout(this.goBackFallbackPage,200);
      },
      goToDownloads() {
        this.didPageBackWork = false;
        this.$root.$emit('trigger-download');
        this.pageBackTimer = setTimeout(this.goBackFallbackPage,200);
      },
      goBackFallbackPage(){
        if (this.didPageBackWork)
          return;

        const guardedBackRoute = this.getGuardedBackRoute();
        if (this.appStore.externalBackGuardArmed && guardedBackRoute) {
          this.navigateToRouteName(guardedBackRoute);
          return;
        }

        const fallbackPage = this.$route.meta.showBackButton;

        if (typeof fallbackPage === "boolean" && fallbackPage) {
          this.$navigateToStartPage();
        } else if (typeof fallbackPage === "string") {
          this.$navigate({ path : fallbackPage });
        }
      },
    },
    created() {
      if (this.appStore.externalEntryRootRouteName && !this.appStore.isExternalEntryRootRoute(this.$route.name)) {
        this.appStore.clearExternalEntryRootRoute();
      }
    },
    watch: {
      // Watch route changes and update the page title accordingly
      $route(to) {
        const safeAnchorRoutes = ['OrderMenuParameters', 'MealPlanParameters', 'Dashboard'];
        if (safeAnchorRoutes.includes(to.name)) {
          this.appStore.clearExternalBackGuard();
        }

        if (this.appStore.externalEntryRootRouteName && !this.appStore.isExternalEntryRootRoute(to.name)) {
          this.appStore.clearExternalEntryRootRoute();
        }

        this.didPageBackWork = true;
        clearTimeout(this.pageBackTimer);
        this.pageBackTimer = undefined;
        this.currentPageTitle = to.meta.title || '';
        this.resetPageMetaTitle();
      },
    },
  };
</script>

<style scoped>
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: sticky;
  }

  .header-left,
  .header-right {
    flex-basis: 15%; /* Set equal width for left and right sections */
    display: flex;
    align-items: center;
    justify-content: flex-start; /* Align items to the start for the left section */
  }

  .header-right {
    justify-content: right;
  }

  .header-middle {
    flex-grow: 1; /* Allow the middle section to grow and take available space */
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 0 10px; /* Add padding to prevent overlap */
  }

  @media (max-width: 768px) {
    .header-middle {
      display: flow-root;
      padding: 0 5px; /* Reduce padding on smaller screens */
    }
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {

    .header-left,
    .header-right {
      flex-basis: 20%; /* Increase size for smaller screens */
    }
  }
</style>
